import React, { memo, useMemo } from "react";

import { useTranslation } from "utils";

import { CROP_DETAILS_ECONOMICS_FIELDS, CROP_DETAILS_MAX_TOTAL_LOSSES_PCT, CropDetailsNumberFieldKey } from "consts";

import { Form } from "components";

import { CropDetailsFieldRules, CropDetailsNumberField } from "pages/app/scenario-analysis/components/crop-details-input-context-drawer/CropDetailsNumberField";
import { CropDetailsFieldGuidance } from "pages/app/scenario-analysis/hooks/useCropDetailsGuidance";

type CropDetailsForm = ReturnType<typeof Form.useForm>[0];

interface CropDetailsEconomicsFieldsProps {
  form: CropDetailsForm;
  initialValues: Partial<Record<CropDetailsNumberFieldKey, number | null>>;
  guidance: CropDetailsFieldGuidance;
}

export const CropDetailsEconomicsFields = memo(({ form, initialValues, guidance }: CropDetailsEconomicsFieldsProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { onFocus, getFieldLabel, getUnitSuffix } = guidance;

  const totalLossesRule: CropDetailsFieldRules = useMemo(
    () => [
      {
        validator: (_: unknown, val: number | null) => {
          const fodder: number = form.getFieldValue("fodderLosses") ?? 0;
          const mould: number = val ?? 0;
          if (fodder + mould > CROP_DETAILS_MAX_TOTAL_LOSSES_PCT) {
            return Promise.reject(new Error(t("cropDetailsDrawer.validation.totalLossesExceeded")));
          }
          return Promise.resolve();
        },
      },
    ],
    [form, t]
  );

  return (
    <section className="crop-details-drawer-section-card">
      <div className="crop-details-drawer-section-card-heading">{t("cropDetailsDrawer.section.economics")}</div>
      <div className="crop-details-drawer-grid crop-details-drawer-grid--2col">
        {CROP_DETAILS_ECONOMICS_FIELDS.map((field) => (
          <CropDetailsNumberField
            key={field.name}
            field={field}
            label={getFieldLabel(field.name)}
            suffix={getUnitSuffix(field.name)}
            initialValue={initialValues[field.name]}
            onFocus={onFocus}
            extraRules={field.name === "mouldLosses" ? totalLossesRule : undefined}
          />
        ))}
      </div>
    </section>
  );
});
